import { useState, useEffect, useCallback, useRef } from "react"
import { useSelector } from "react-redux"
import { apiService } from "../../features/shared/apiService"
import { Provider } from "../Provider/schema"
import { DataStatus } from "../enums"
import { selectProvider } from "../Provider/model"
import appInsightsLogger from "../../features/logging/appInsightsLogger"

type UseUsersDataForUserIdsType = {
  useProviderDataStatus: DataStatus
  provider: Provider | null
  refetchProvider: () => Promise<void>
}

const LOADING_STATUS = DataStatus.LOADING
const LOADED_STATUS = DataStatus.LOADED
const ERROR_STATUS = DataStatus.ERROR

// Module-level cache: deduplicates concurrent calls for the same providerId
// (e.g. many UnregisteredBadge rows mounting simultaneously).
const providerRequestCache = new Map<string, Promise<Provider>>()

function useProviderData(
  providerId: string | null,
): UseUsersDataForUserIdsType {
  const [provider, setProvider] = useState<Provider | null>(null)
  const [dataStatus, setDataStatus] = useState<DataStatus>(LOADING_STATUS)
  // Each fetch is stamped with a unique Symbol. The Redux sync effect clears
  // this ref to signal that an in-flight fetch should not apply its result —
  // preventing a stale cache resolution from overwriting fresh Redux data.
  const fetchTokenRef = useRef<symbol | null>(null)

  const fetchProvider = useCallback(async () => {
    if (!providerId) {
      setProvider(null)
      setDataStatus(LOADED_STATUS)
      return
    }

    setDataStatus(LOADING_STATUS)
    setProvider(null)
    const token = Symbol()
    fetchTokenRef.current = token

    try {
      if (!providerRequestCache.has(providerId)) {
        providerRequestCache.set(
          providerId,
          apiService.provider.get.PROVIDER_BY_ID({ providerId }),
        )
      }
      const newProvider = await providerRequestCache.get(providerId)!
      if (fetchTokenRef.current === token) {
        setProvider(newProvider)
        setDataStatus(LOADED_STATUS)
      }
    } catch (error) {
      if (fetchTokenRef.current === token) {
        providerRequestCache.delete(providerId)
        appInsightsLogger.traceError(error)
        setDataStatus(ERROR_STATUS)
      }
    }
  }, [providerId])

  useEffect(() => {
    fetchProvider()
  }, [fetchProvider])

  // When the Redux store has a fresher copy of this provider, sync local state and the module cache immediately.
  // Clearing the token cancels any in-flight fetch so its stale resolution cannot overwrite the data we just applied.
  const reduxProvider = useSelector(selectProvider)
  useEffect(() => {
    if (!reduxProvider || !providerId) return
    if (reduxProvider.id !== providerId) return
    fetchTokenRef.current = null
    providerRequestCache.set(providerId, Promise.resolve(reduxProvider))
    setProvider(reduxProvider)
    setDataStatus(LOADED_STATUS)
  }, [reduxProvider, providerId])

  const refetchProvider = useCallback((): Promise<void> => {
    if (providerId) providerRequestCache.delete(providerId)
    return fetchProvider()
  }, [fetchProvider, providerId])

  return {
    provider,
    useProviderDataStatus: dataStatus,
    refetchProvider,
  }
}

export default useProviderData
